import React, { useEffect, useState } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import { Container, Button, Form } from "react-bootstrap";
import Header from "../_Common/header";
import FleetService from "../../services/fleetService";
import "./fleet-detail.scss";

const categoryInfo = {
    sail_boats_tab: { type: "Sail Boat", icon: "ri-sailboat-line" },
    sail_yachts_tab: { type: "Sail Yacht", icon: "ri-ship-line" },
    motor_yachts_tab: { type: "Motor Yacht", icon: "ri-ship-2-line" },
    speed_boats_tab: { type: "Speed Boat", icon: "ri-speed-mini-line" },
    ferry_boats_tab: { type: "Ferry Boat", icon: "ri-ferry-line" }
};

export default function FleetDetail() {
    const { slug } = useParams();
    const navigate = useNavigate();
    const location = useLocation();
    const [boat, setBoat] = useState(null);

    // Booking Form State
    const [bookingDate, setBookingDate] = useState("");
    const [selectedTrip, setSelectedTrip] = useState("");
    const [guestCount, setGuestCount] = useState(1);

    // Lightbox State
    const [lightboxOpen, setLightboxOpen] = useState(false);
    const [lightboxIndex, setLightboxIndex] = useState(0);
    const [mobileGalleryIndex, setMobileGalleryIndex] = useState(0);
    const [tripDropdownOpen, setTripDropdownOpen] = useState(false);

    // Swipe State
    const [touchStart, setTouchStart] = useState(null);
    const [touchEnd, setTouchEnd] = useState(null);



    // Floating WhatsApp State
    const [showWhatsAppInput, setShowWhatsAppInput] = useState(false);
    const [whatsAppMessage, setWhatsAppMessage] = useState("");

    const images = boat ? (boat.gallery && boat.gallery.length > 0 ? boat.gallery : [boat.image]) : [];

    useEffect(() => {
        window.scrollTo(0, 0);
        if (slug) {
            const data = FleetService.getFleetBySlug(slug);
            if (data) {
                setBoat(data);
                if (Array.isArray(data.trip) && data.trip.length > 0) {
                    setSelectedTrip(data.trip[0]);
                } else if (typeof data.trip === 'string') {
                    setSelectedTrip(data.trip);
                }
            } else {
                console.error("Boat not found");
                navigate("/full-fleet");
            }
        }
    }, [slug, navigate]);

    if (!boat) return <div className="p-5 text-center">Loading...</div>;

    const info = categoryInfo[boat.category] || { type: "Yacht", icon: "ri-anchor-line" };
    const trips = Array.isArray(boat.trip) ? boat.trip : [boat.trip];
    const capacityNum = parseInt(boat.guestCount.replace(/\D/g, '')) || 10;

    const defaultPoints = [
        "Premium Sound System",
        "Ice Box & Coolers",
        "Experienced Crew",
        "Life Jackets & Safety Gear"
    ];

    const handleWhatsAppEnquiry = (e) => {
        e.preventDefault();
        const formattedDate = bookingDate ? new Date(bookingDate).toLocaleDateString('en-GB', {
            day: 'numeric', month: 'long', year: 'numeric'
        }) : "Date Not Selected";

        const message = `Hi Yacht Mumbai! I'd like to book:
    
🚢 Fleet: ${info.type} - ${boat.name}
📍 Trip: ${selectedTrip}
👥 Guests: ${guestCount}
📅 Date: ${formattedDate}
    
Please share availability and pricing details.`;

        const encodedMessage = encodeURIComponent(message);
        window.open(`https://wa.me/919920088412?text=${encodedMessage}`, '_blank');
    };

    const handleGuestChange = (delta) => {
        const newVal = guestCount + delta;
        if (newVal >= 1 && newVal <= capacityNum) {
            setGuestCount(newVal);
        }
    };

    const openLightbox = (index) => {
        setLightboxIndex(index);
        setLightboxOpen(true);
    };

    const closeLightbox = () => {
        setLightboxOpen(false);
    };

    const nextImage = (e) => {
        e.stopPropagation();
        setLightboxIndex((prev) => (prev + 1) % images.length);
    };

    const prevImage = (e) => {
        e.stopPropagation();
        setLightboxIndex((prev) => (prev - 1 + images.length) % images.length);
    };

    const handleBack = () => {
        const params = new URLSearchParams(location.search);
        const category = params.get('category');
        if (category) {
            navigate(`/full-fleet?category=${category}`);
        }
    };



    const nextMobileImage = () => {
        setMobileGalleryIndex((prev) => (prev + 1) % images.length);
    };

    const prevMobileImage = () => {
        setMobileGalleryIndex((prev) => (prev - 1 + images.length) % images.length);
    };

    const handleTripSelect = (trip) => {
        setSelectedTrip(trip);
        setTripDropdownOpen(false);
    };

    const handleFloatingMessageSend = (e) => {
        e.preventDefault();
        const msg = encodeURIComponent(whatsAppMessage);
        window.open(`https://wa.me/919920088412?text=${msg}`, '_blank');
        setWhatsAppMessage("");
        setShowWhatsAppInput(false);
    };

    // Swipe Handlers
    const minSwipeDistance = 50;

    const onTouchStart = (e) => {
        setTouchEnd(null);
        setTouchStart(e.targetTouches[0].clientX);
    };

    const onTouchMove = (e) => {
        setTouchEnd(e.targetTouches[0].clientX);
    };

    const onTouchEnd = () => {
        if (!touchStart || !touchEnd) return;

        const distance = touchStart - touchEnd;
        const isLeftSwipe = distance > minSwipeDistance;
        const isRightSwipe = distance < -minSwipeDistance;

        if (isLeftSwipe) {
            setLightboxIndex((prev) => (prev + 1) % images.length);
        }
        if (isRightSwipe) {
            setLightboxIndex((prev) => (prev - 1 + images.length) % images.length);
        }
    };



    return (
        <React.Fragment>
            <Header />



            <div className="fleet-detail-page">
                {/* HERO HEADER SECTION */}
                <div className="hero-header-section">
                    <Container>
                        <div className="hero-top-nav">
                            <button className="back-btn" onClick={handleBack}>
                                <i className="ri-arrow-left-line"></i> Back
                            </button>
                            <span className="fleet-type-badge">{info.type}</span>
                        </div>
                        <div className="hero-content">
                            <h1>{boat.name}</h1>
                            <div className="deco-line"></div>
                        </div>
                    </Container>
                    <div className="hero-overlay"></div>
                </div>

                <Container>
                    {/* ABOUT SECTION */}
                    <div className="about-section-container">
                        <h3>About This Fleet</h3>
                        <p>{boat.about}</p>
                    </div>

                    {/* FLEET SPECIFICATIONS GRID */}
                    <div className="specs-grid-section">
                        {/* POINTS CARD */}
                        <div className="spec-card">
                            <div className="card-icon"><i className="ri-list-check-2"></i></div>
                            <div className="card-label">POINTS</div>
                            <ul className="points-list">
                                {boat.points && boat.points.map((point, index) => (
                                    <li key={index}><i className="ri-checkbox-circle-fill"></i> {point}</li>
                                ))}
                            </ul>
                        </div>

                        {/* TRIPS CARD */}
                        <div className="spec-card">
                            <div className="card-icon"><i className="ri-map-pin-range-line"></i></div>
                            <div className="card-label">TRIPS</div>
                            <ul className="trips-list">
                                {trips.map((t, i) => (
                                    <li key={i}>
                                        <i className="ri-map-pin-fill"></i>
                                        <span>{t}</span>
                                    </li>
                                ))}
                            </ul>
                        </div>

                        {/* CAPACITY CARD */}
                        <div className="spec-card capacity-card">
                            <div className="card-icon"><i className="ri-group-fill"></i></div>
                            <div className="card-label">CAPACITY</div>
                            <div className="capacity-number">{capacityNum}</div>
                            <div className="capacity-text">Guests</div>
                        </div>
                    </div>

                    {/* GALLERY SECTION */}
                    <div className="gallery-section-container">
                        <h3>Gallery</h3>
                        <div className="bento-grid-gallery">
                            <div className="gallery-item item-main" onClick={() => openLightbox(0)}>
                                <img src={images[0]} alt={boat.name} />
                                <div className="overlay"><i className="ri-add-line"></i></div>
                            </div>
                            {images.slice(1, 4).map((img, idx) => (
                                <div className="gallery-item item-sub" key={idx} onClick={() => openLightbox(idx + 1)}>
                                    <div className="placeholder-image-wrapper">
                                        <img src={img} alt={`Gallery ${idx + 1}`} className="sub-img" />
                                        <div className="overlay"><i className="ri-add-line"></i></div>
                                    </div>
                                </div>
                            ))}
                        </div>

                        {/* MOBILE SLIDER */}
                        <div className="mobile-gallery-slider">
                            <div className="slider-wrapper">
                                <button className="nav-btn prev" onClick={prevMobileImage}><i className="ri-arrow-left-s-line"></i></button>
                                <div className="curr-img" onClick={() => openLightbox(mobileGalleryIndex)}>
                                    <img src={images[mobileGalleryIndex]} alt="Fleet Mobile" />
                                </div>
                                <button className="nav-btn next" onClick={nextMobileImage}><i className="ri-arrow-right-s-line"></i></button>
                            </div>
                            <div className="slider-dots">
                                {images.map((_, i) => (
                                    <span key={i} className={i === mobileGalleryIndex ? 'active' : ''}></span>
                                ))}
                            </div>
                        </div>
                    </div>

                    {/* BOOKING FORM SECTION */}
                    <div className="booking-form-section">
                        <div className="form-container">
                            <h3 className="section-title">Book Your Experience</h3>
                            <p className="subtitle">Secure your voyage on the {boat.name} today</p>

                            <Form onSubmit={handleWhatsAppEnquiry}>
                                <div className="form-group-custom">
                                    <label> <i className="ri-calendar-event-line"></i> SELECT YOUR DATE</label>
                                    <Form.Control
                                        type="date"
                                        required
                                        value={bookingDate}
                                        onChange={(e) => setBookingDate(e.target.value)}
                                        min={new Date().toISOString().split('T')[0]}
                                    />
                                </div>

                                <div className="form-group-custom select-wrapper custom-dropdown-container">
                                    <label> <i className="ri-ship-line"></i> CHOOSE YOUR TRIP</label>

                                    <div
                                        className="custom-select-display"
                                        onClick={() => setTripDropdownOpen(!tripDropdownOpen)}
                                    >
                                        <span>{selectedTrip || "Select a trip..."}</span>
                                        <i className={`ri-arrow-down-s-line custom-arrow ${tripDropdownOpen ? 'rotate' : ''}`}></i>
                                    </div>

                                    {tripDropdownOpen && (
                                        <div className="custom-dropdown-list">
                                            {trips.map((t, idx) => (
                                                <div
                                                    key={idx}
                                                    className={`dropdown-item ${selectedTrip === t ? 'active' : ''}`}
                                                    onClick={() => handleTripSelect(t)}
                                                >
                                                    {t}
                                                </div>
                                            ))}
                                        </div>
                                    )}
                                </div>

                                <div className="form-group-custom">
                                    <label> <i className="ri-group-line"></i> NUMBER OF GUESTS (Max: {capacityNum})</label>
                                    <div className="guest-input-wrapper">
                                        <button type="button" onClick={() => handleGuestChange(-1)} disabled={guestCount <= 1}>-</button>
                                        <span className="guest-val">{guestCount}</span>
                                        <button type="button" onClick={() => handleGuestChange(1)} disabled={guestCount >= capacityNum}>+</button>
                                    </div>
                                </div>

                                <Button type="submit" className="enquire-btn">
                                    <i className="ri-whatsapp-line"></i> ENQUIRE NOW ON WHATSAPP
                                </Button>
                            </Form>
                        </div>
                    </div>

                    {/* FOOTER TAGLINE */}
                    <div className="brand-footer-tagline">
                        <div className="wave-deco"><i className="ri-anchor-fill"></i></div>
                        <h2>Mumbai's Premier Yacht Experience</h2>
                    </div>

                </Container>

                {/* LIGHTBOX MODAL */}
                {
                    lightboxOpen && (
                        <div className="lightbox-overlay" onClick={closeLightbox}>
                            <button className="close-btn" onClick={closeLightbox}><i className="ri-close-line"></i></button>

                            <div
                                className="lightbox-content"
                                onClick={e => e.stopPropagation()}
                                onTouchStart={onTouchStart}
                                onTouchMove={onTouchMove}
                                onTouchEnd={onTouchEnd}
                            >
                                <button className="nav-btn prev" onClick={prevImage}><i className="ri-arrow-left-s-line"></i></button>
                                <img src={images[lightboxIndex]} alt="Gallery View" />
                                <button className="nav-btn next" onClick={nextImage}><i className="ri-arrow-right-s-line"></i></button>
                            </div>

                            <div className="image-counter">
                                {lightboxIndex + 1} / {images.length}
                            </div>
                        </div>
                    )
                }
                {/* FLOATING WHATSAPP BUTTON */}
                <div className={`floating-whatsapp-container ${showWhatsAppInput ? 'open' : ''}`}>
                    {showWhatsAppInput && (
                        <div className="whatsapp-input-files">
                            <button className="close-input" onClick={() => setShowWhatsAppInput(false)}>
                                <i className="ri-close-line"></i>
                            </button>
                            <form onSubmit={handleFloatingMessageSend}>
                                <textarea
                                    placeholder="Type your message..."
                                    value={whatsAppMessage}
                                    onChange={e => setWhatsAppMessage(e.target.value)}
                                    autoFocus
                                />
                                <button type="submit" className="send-fab-btn">
                                    <i className="ri-send-plane-fill"></i>
                                </button>
                            </form>
                        </div>
                    )}
                    <button
                        className="whatsapp-fab-main"
                        onClick={() => setShowWhatsAppInput(!showWhatsAppInput)}
                    >
                        <i className="ri-whatsapp-line"></i>
                    </button>
                </div>

            </div >
        </React.Fragment >
    );
}
